import type { PulseStageCycle } from "@/lib/six-pillars-stage";

export type SixPillarPreviewId =
  | "overview"
  | "coop-profile"
  | "fundamentals"
  | "pulse"
  | "candidates"
  | "random-coffee";

export type PreviewSignalGroups = {
  profiles: string[];
  fundamentals: string[];
  pulse: string[];
};

export type PreviewPerson = {
  name: string;
  role: string;
  type: "lead" | "member" | "candidate";
  isCoLead: boolean;
  pronouns: {
    subject: "he" | "she" | "they";
    object: "him" | "her" | "them";
    possessive: "his" | "her" | "their";
  };
  avatarSrc: string;
  profileTitle: string;
  profileAxes: string[];
  fundamentalsCompleted: number;
  fundamentalsTotal: number;
  checkinsCompleted: number;
  checkinsTotal: number;
  avatarTone: "sand" | "sky" | "rose" | "mint" | "violet";
  candidateStage?: "Completed Team Fit";
};

export type PreviewListItem =
  | string
  | {
      text: string;
      meta?: string;
    };

export type PreviewSection = {
  id: string;
  title: string;
  preview: string;
  paragraphs?: string[];
  groups?: Array<{
    title: string;
    badge?: string;
    tone?: "neutral" | "positive" | "warning" | "info";
    paragraphs?: string[];
    lists?: Array<{
      label?: string;
      style?: "default" | "actionable";
      items: PreviewListItem[];
    }>;
  }>;
  lists?: Array<{
    label?: string;
    style?: "default" | "actionable";
    items: PreviewListItem[];
  }>;
};

export type RandomCoffeePreviewInvite = {
  id: string;
  /** Monday of the meeting week, `YYYY-MM-DD`. */
  weekStart: string;
  /** The pairing format as the product names it. */
  format: string;
  partnerName: string;
  partnerRole: string;
  partnerEmail: string;
  teamName: string;
  profileLabel: string;
  reason: string;
  conversationGuide: string;
  workTopics: string[];
  lifestyleTopics: string[];
};

/** One of the partner's 30-second cards, written for the viewer who leads them. */
export type RandomCoffeePreviewDeck = {
  glyph: string;
  headline: string;
  reason: string;
  /** One line per function, in the profile's order (strongest first). */
  functionLines: Record<string, string>;
  do: Array<{ title: string; detail: string }>;
  dont: Array<{ title: string; detail: string }>;
};

export type OverviewSectionId = "issues" | "opportunities" | "strengths";

export type OverviewPreviewItem = {
  id: string;
  title: string;
  whyItMatters: string;
  evidence: string;
  quickWin: string;
  longTermMove: string;
  suggestedOwner: string;
  whyThisOwner: string;
  signals: PreviewSignalGroups;
};

export type OverviewPreviewGroup = {
  id: OverviewSectionId;
  label: string;
  items: OverviewPreviewItem[];
};

export type RoadmapLaneKey = "today" | "this_week" | "this_month" | "this_quarter";

/** A 1-based insight reference from the product fixture, such as `issues:2`. */
export type OverviewInsightRef = `${OverviewSectionId}:${number}`;

export type OverviewRoadmapItem = {
  id: string;
  title: string;
  whyItMatters: string;
  doneWhen: string;
  /** The insights this step answers. */
  addresses: OverviewInsightRef[];
  suggestedOwner: string;
  whyThisOwner: string;
  signals: PreviewSignalGroups;
};

export type OverviewSignalSource = keyof PreviewSignalGroups;

export type OverviewSyncProvider = "asana" | "notion" | "clickup" | "linear";

export type OverviewPreviewSnapshot = {
  teamName: string;
  /** The demo is seen through the team lead's eyes: his items say "You". */
  viewer: string;
  /** When the overview was generated; the goals and insights are "as of" this date. */
  generatedOn: string;
  goals: string[];
  currentRisk: {
    level: "low" | "medium" | "high";
    levelLabel: string;
    headline: string;
    headlineReason: string;
    rationale: string;
    confidence: "low" | "medium" | "high";
    sources: OverviewSignalSource[];
    limitations: string[];
  };
  groups: OverviewPreviewGroup[];
  roadmap: Record<RoadmapLaneKey, OverviewRoadmapItem[]>;
  sync: {
    providers: OverviewSyncProvider[];
    lastSync: { provider: OverviewSyncProvider; at: string };
  };
};

export type FundamentalsCategoryScore = {
  category: string;
  average: number;
};

export type PulseFeedbackItem = {
  id: string;
  when: string;
  text: string;
};

export const sixPillarsPreviewPeople: PreviewPerson[] = [
  {
    name: "Adrian Cole",
    role: "Team Lead",
    type: "lead",
    isCoLead: false,
    pronouns: { subject: "he", object: "him", possessive: "his" },
    avatarSrc: "/cooperly/people/adrian-cole.png",
    profileTitle: "Planner",
    profileAxes: ["R", "D", "Ca", "C"],
    fundamentalsCompleted: 10,
    fundamentalsTotal: 12,
    checkinsCompleted: 28,
    checkinsTotal: 32,
    avatarTone: "sand",
  },
  {
    name: "Serena Holt",
    role: "Designer",
    type: "member",
    isCoLead: true,
    pronouns: { subject: "she", object: "her", possessive: "her" },
    avatarSrc: "/cooperly/people/serena-holt.png",
    profileTitle: "Empath",
    profileAxes: ["C", "Ca", "D", "R"],
    fundamentalsCompleted: 9,
    fundamentalsTotal: 12,
    checkinsCompleted: 24,
    checkinsTotal: 29,
    avatarTone: "sky",
  },
  {
    name: "Priya Dawson",
    role: "Product Manager",
    type: "member",
    isCoLead: false,
    pronouns: { subject: "she", object: "her", possessive: "her" },
    avatarSrc: "/cooperly/people/priya-dawson.png",
    profileTitle: "Enforcer",
    profileAxes: ["D", "R", "Ca", "C"],
    fundamentalsCompleted: 8,
    fundamentalsTotal: 11,
    checkinsCompleted: 26,
    checkinsTotal: 31,
    avatarTone: "rose",
  },
  {
    name: "Felix Mercer",
    role: "Front-End Developer",
    type: "member",
    isCoLead: false,
    pronouns: { subject: "he", object: "him", possessive: "his" },
    avatarSrc: "/cooperly/people/felix-mercer.png",
    profileTitle: "Poet",
    profileAxes: ["C", "R", "D", "Ca"],
    fundamentalsCompleted: 9,
    fundamentalsTotal: 12,
    checkinsCompleted: 23,
    checkinsTotal: 28,
    avatarTone: "mint",
  },
  {
    name: "Julian Park",
    role: "Engineer",
    type: "member",
    isCoLead: false,
    pronouns: { subject: "he", object: "him", possessive: "his" },
    avatarSrc: "/manual/avatars/avatar_04.png",
    profileTitle: "Engineer",
    profileAxes: ["R", "Ca", "C", "D"],
    fundamentalsCompleted: 9,
    fundamentalsTotal: 12,
    checkinsCompleted: 21,
    checkinsTotal: 26,
    avatarTone: "violet",
  },
  {
    name: "James Bremer",
    role: "Senior Backend Engineer",
    type: "candidate",
    isCoLead: false,
    pronouns: { subject: "he", object: "him", possessive: "his" },
    avatarSrc: "/manual/avatars/avatar_02.png",
    profileTitle: "Empath",
    profileAxes: ["C", "Ca", "D", "R"],
    fundamentalsCompleted: 0,
    fundamentalsTotal: 0,
    checkinsCompleted: 0,
    checkinsTotal: 0,
    avatarTone: "sand",
    candidateStage: "Completed Team Fit",
  },
];

// Insights and roadmap text follow the product's EN demo fixture
// (cooperly_app resources/demo-data/overview.json). Goals, confidence, sync and
// dates are site-only demo values; the date matches the last Pulse cycle below.
export const sixPillarsOverviewPreview: OverviewPreviewSnapshot = {
  teamName: "Demo Team",
  viewer: "Adrian Cole",
  generatedOn: "6 Apr 2026",
  goals: [
    "Reach 70% AI-assisted coding across the team by the end of the year.",
    "Grow the messaging product's monthly revenue every month this year.",
  ],
  currentRisk: {
    level: "high",
    levelLabel: "High risk",
    headline: "Decisions and priorities still flow through too few people.",
    headlineReason:
      "Adrian Cole and Serena Holt still settle most priorities and package the final feedback, so work waits for them.",
    rationale:
      "Demo Team has enough talent to move faster than it currently does, but the operating rhythm still depends too heavily on Adrian Cole and Serena Holt to align priorities, resolve ambiguity, and package final feedback. Pulse points to late clarification and fragmented handoffs, Fundamentals point to uneven accountability and too little productive conflict, and the broader team signal suggests a strong specialist core without an equally strong middle layer. Until that changes, the team will keep burning time on waiting, second-guessing, and rebuilding work that was already in motion.",
    confidence: "medium",
    sources: ["profiles", "fundamentals", "pulse"],
    limitations: [
      "Pulse covers the four most recent cycles, so patterns older than that do not show up here.",
      "Fundamentals has two completed runs; a third would show whether the accountability gap is closing.",
    ],
  },
  groups: [
    {
      id: "issues",
      label: "Issues",
      items: [
        {
          id: "issue-1",
          title: "Decision ownership is still too concentrated",
          whyItMatters:
            "The team loses pace whenever too many choices route back through Adrian Cole and Serena Holt. That creates avoidable waiting time, keeps ownership fuzzy, and makes the delivery rhythm more fragile than the work itself requires.",
          evidence:
            "The source patterns consistently point to centralized decisions, missing middle ownership, and repeated escalation on work that should already have a named local owner. The team does not lack judgment; it lacks enough decision rights distributed below the top layer.",
          quickWin:
            "Publish a one-page decision map that separates what must escalate from what can be closed by the working team.",
          longTermMove:
            "Build a lightweight middle layer so recurring delivery, planning, and quality calls stop depending on the same two people.",
          suggestedOwner: "Adrian Cole",
          whyThisOwner:
            "Adrian Cole is best positioned to redraw decision boundaries without creating ambiguity about the final escalation path.",
          signals: {
            profiles: [
              "The team reads as expert-heavy and capable, but too much clarity still lives at the top.",
              "There is enough craft depth to distribute more decisions safely.",
            ],
            fundamentals: [
              "Accountability is not yet evenly distributed.",
              "Conflict is not strong enough to resolve ambiguous ownership early.",
            ],
            pulse: [
              "People keep pointing to late approvals and unclear responsibility.",
              "Several signals describe waiting for alignment that should already exist.",
            ],
          },
        },
        {
          id: "issue-2",
          title: "Feedback still arrives in fragments instead of one clear pass",
          whyItMatters:
            "When comments come late or in pieces, the team turns healthy iteration into avoidable rework. The cost is not only time; it also erodes confidence that a finished handoff is actually finished.",
          evidence:
            "The strongest operating signal is not low capability but scattered clarification. The source data repeatedly points to partial feedback, shifting comments, and work being reopened after the team thought the brief was stable.",
          quickWin:
            "Require one consolidated feedback pass with priority, owner, and response deadline before major revisions begin.",
          longTermMove:
            "Standardize how work is briefed, reviewed, and signed off so handoffs do not rely on personal memory or ad hoc chat threads.",
          suggestedOwner: "Priya Dawson",
          whyThisOwner:
            "Priya Dawson is the best fit to turn repeated feedback pain into one visible operating rule the whole team can follow.",
          signals: {
            profiles: [
              "The team can work within clear structure once the structure is explicit.",
              "A strong specialist team benefits disproportionately from a shared review format.",
            ],
            fundamentals: [
              "Commitment weakens when next steps stay implied.",
              "Results suffer when standards are interpreted differently by different people.",
            ],
            pulse: [
              "Signals repeatedly mention fragmented comments and late adjustments.",
              "The team wants fewer surprises and one cleaner handoff.",
            ],
          },
        },
        {
          id: "issue-3",
          title: "Risk and workload are being surfaced too late",
          whyItMatters:
            "The team is not only busy; it is busy too reactively. Without earlier visibility into overload and slipping work, the group keeps discovering red-zone projects after the strain is already personal and expensive.",
          evidence:
            "The source narrative includes repeated stress signals, delivery pressure, and too little early escalation. People appear willing to help each other, but the system still raises risk after the team has already absorbed the cost.",
          quickWin:
            "Run a weekly red-zone review across active work and force an explicit reprioritization before overload becomes the default.",
          longTermMove:
            "Add basic capacity tracking, early risk thresholds, and a stop-loss rule for work that is sliding without a real owner.",
          suggestedOwner: "Serena Holt",
          whyThisOwner:
            "Serena Holt is well placed to make risk visible earlier because the problem sits at the intersection of coordination, pacing, and cross-team follow-through.",
          signals: {
            profiles: [
              "The team can push hard, but not indefinitely without clearer operating guardrails.",
              "A small expert core becomes fragile when practical pacing is left implicit.",
            ],
            fundamentals: [
              "Trust is usable, but it does not compensate for weak escalation habits.",
              "Results drop when accountability arrives after the fact.",
            ],
            pulse: [
              "The team is signaling strain, not just inconvenience.",
              "People describe deadline pressure appearing too late in the cycle.",
            ],
          },
        },
        {
          id: "issue-4",
          title: "Role boundaries and growth paths are still too blurry",
          whyItMatters:
            "When people cannot tell where their lane begins, where it ends, and how it can grow, motivation drops long before performance visibly fails. That makes retention and sustainable delegation harder than they need to be.",
          evidence:
            "The source material points to uneven clarity around responsibilities, underused potential, and uncertainty about how roles evolve. This is not a culture problem first; it is a clarity problem.",
          quickWin:
            "Run a focused round of role conversations that ends with written ownership, growth edges, and escalation expectations.",
          longTermMove:
            "Turn those conversations into a repeatable ownership model with visible lanes, expectations, and progression rules.",
          suggestedOwner: "Adrian Cole",
          whyThisOwner:
            "Adrian Cole can connect team-level design, day-to-day expectations, and growth decisions without splitting them into separate conversations.",
          signals: {
            profiles: [
              "There is enough range in the team for clearer specialization and delegation.",
              "The group looks stronger than the current lane definition suggests.",
            ],
            fundamentals: [
              "Commitment suffers when ownership is felt differently by different people.",
              "Accountability stays uneven when role edges are not explicit.",
            ],
            pulse: [
              "People are asking for clearer expectations, not just more motivation.",
              "The team is signaling uncertainty about how potential gets used.",
            ],
          },
        },
      ],
    },
    {
      id: "opportunities",
      label: "Opportunities",
      items: [
        {
          id: "opportunity-1",
          title: "There are already credible seeds of a middle layer inside the team",
          whyItMatters:
            "The team does not need a full reorganization before it can reduce top-level bottlenecks. It already has enough experienced people to test local ownership in a few recurring lanes.",
          evidence:
            "The source signals do not describe an empty bench. They describe capable people working inside a system that still routes too much through the same narrow path.",
          quickWin:
            "Pilot named owners for two or three recurring lanes instead of waiting for a formal restructure.",
          longTermMove:
            "Promote those pilots into a durable middle layer once the boundaries, rituals, and decision rights prove they work.",
          suggestedOwner: "Adrian Cole",
          whyThisOwner:
            "Adrian Cole can sponsor pilot ownership without making it feel like an unofficial burden with no real authority.",
          signals: {
            profiles: [
              "The team has enough depth to support delegated ownership.",
              "Several people look capable of carrying a defined lane.",
            ],
            fundamentals: [
              "Trust is strong enough to support structured experimentation.",
              "Accountability should improve once owners are named clearly.",
            ],
            pulse: [
              "The team is asking for clearer structure, not more meetings.",
              "There is appetite for process fixes that reduce chaos.",
            ],
          },
        },
        {
          id: "opportunity-2",
          title: "One shared status and handoff rhythm would create immediate lift",
          whyItMatters:
            "The team does not need a complex operating system to feel better quickly. A visible weekly rhythm for status, blockers, and handoffs would remove a surprising amount of ambiguity.",
          evidence:
            "The strongest recurring signals point to the same gap: work moves, but the condition of work is not visible early enough or consistently enough to the whole team.",
          quickWin:
            "Start one weekly project status ritual with owners, blockers, next decisions, and explicit risk flags.",
          longTermMove:
            "Tie that ritual to one repeatable handoff pattern so status visibility and handoff quality reinforce each other.",
          suggestedOwner: "Priya Dawson",
          whyThisOwner:
            "Priya Dawson can keep the ritual practical and outcome-focused rather than letting it turn into reporting theatre.",
          signals: {
            profiles: [
              "The team looks capable of working from a simple shared structure.",
              "Clarity gains should compound quickly in a small group.",
            ],
            fundamentals: [
              "Commitment improves when decisions, owners, and deadlines are made visible.",
              "Results improve when risks are surfaced before they become surprises.",
            ],
            pulse: [
              "People want visibility into current status, not more interpretation after the fact.",
              "Late clarification keeps showing up as the same avoidable drag.",
            ],
          },
        },
        {
          id: "opportunity-3",
          title: "The team's craft strength can be turned into a repeatable system",
          whyItMatters:
            "Right now a lot of quality still lives in individual judgment. That is useful, but it does not scale nearly as well as shared review rules, clearer defaults, and reusable operating standards.",
          evidence:
            "The source signals show strong specialist capability alongside process friction. That combination usually means the team can improve quickly if it codifies what good already looks like.",
          quickWin:
            "Document the few review and handoff rules the team already trusts and use them on every active lane.",
          longTermMove:
            "Turn those rules into a small operating playbook that makes quality less person-dependent over time.",
          suggestedOwner: "Felix Mercer",
          whyThisOwner:
            "Felix Mercer is well placed to convert tacit craft judgment into standards the rest of the team can actually use.",
          signals: {
            profiles: [
              "The team has enough craft maturity to benefit from standards instead of resisting them.",
              "Specialist quality is already a strength worth codifying.",
            ],
            fundamentals: [
              "Results improve when quality expectations are visible and shared.",
              "Accountability rises when standards stop living only in a few heads.",
            ],
            pulse: [
              "People are frustrated by inconsistency more than by the existence of standards.",
              "The team would benefit from fewer custom interpretations of the same work.",
            ],
          },
        },
      ],
    },
    {
      id: "strengths",
      label: "Strengths",
      items: [
        {
          id: "strength-1",
          title: "Demo Team already has a credible quality bar and real delivery ambition",
          whyItMatters:
            "This is not a team that needs motivation before it needs clarity. The core capability is already visible, which means operating improvements should translate into noticeable gains rather than marginal ones.",
          evidence:
            "The source material consistently points to strong specialist skill, serious standards, and people who care about the quality of the final outcome even when the process around them is still messy.",
          quickWin:
            "Use the existing quality bar as the foundation for clearer ways of working, instead of treating process and craft as separate conversations.",
          longTermMove:
            "Build a system where quality remains high even when the lead is not personally stitching every decision together.",
          suggestedOwner: "Julian Park",
          whyThisOwner:
            "Julian Park can help translate quality expectations into repeatable working standards without lowering the bar.",
          signals: {
            profiles: [
              "The team reads as capable, thoughtful, and high-standard.",
              "This is a strength worth systematizing rather than replacing.",
            ],
            fundamentals: [
              "Trust and commitment are strong enough to support a more disciplined system.",
              "The team already behaves like it wants to deliver well.",
            ],
            pulse: [
              "Even negative signals still assume that quality matters.",
              "People are pushing for a better system, not for lower expectations.",
            ],
          },
        },
        {
          id: "strength-2",
          title: "There is enough range in the team to support real delegation",
          whyItMatters:
            "The group is not one-note. It has enough variety in style and capability that clearer ownership can be distributed instead of permanently routed upward.",
          evidence:
            "The source data points to a team that already contains planning, coordination, and specialist depth. The main constraint is not lack of talent; it is lack of an operating shape that trusts that talent consistently.",
          quickWin:
            "Name local owners for the lanes that already have the clearest natural stewards.",
          longTermMove:
            "Turn delegation into a durable pattern with explicit authority, review points, and escalation rules.",
          suggestedOwner: "Adrian Cole",
          whyThisOwner:
            "Adrian Cole has the clearest view of where delegation is already safe and where it still needs support.",
          signals: {
            profiles: [
              "The team has enough complementary capability to avoid top-layer dependence.",
              "Delegation looks blocked more by structure than by skill.",
            ],
            fundamentals: [
              "Trust is high enough to support broader ownership.",
              "Results should improve once delegation becomes explicit.",
            ],
            pulse: [
              "The team wants clearer roles and more predictable ownership.",
              "Several pain points would soften quickly with named local owners.",
            ],
          },
        },
        {
          id: "strength-3",
          title: "Trust is usable enough to change the system without a major fracture",
          whyItMatters:
            "The team is under strain, but it does not read as politically broken. That matters because operational changes will move faster in a team that can still hear hard truths without immediately collapsing into blame.",
          evidence:
            "The source signals show frustration, stress, and inconsistency, but they do not show a team that has given up on one another. That is a practical asset.",
          quickWin:
            "Use the next few process changes to prove that the system can change in response to real pain, not just in response to slogans.",
          longTermMove:
            "Protect that trust by making future tensions visible early and resolving them through clearer ownership instead of more heroics.",
          suggestedOwner: "Serena Holt",
          whyThisOwner:
            "Serena Holt can help the team make operational changes feel grounded and responsive rather than imposed from above.",
          signals: {
            profiles: [
              "The team has enough human range to stay workable under tension.",
              "This is a team that can absorb clearer structure if the intent is visible.",
            ],
            fundamentals: [
              "Trust remains stronger than the lowest operating categories.",
              "That gives the team a real base for change.",
            ],
            pulse: [
              "People are still sending specific improvement signals instead of checking out.",
              "The tone points to repairable frustration, not collapse.",
            ],
          },
        },
      ],
    },
  ],
  roadmap: {
    today: [
      {
        id: "step-1",
        title: "Freeze one shared format for feedback and revisions",
        whyItMatters:
          "The team needs one cleaner comment path immediately so work stops getting reopened through scattered clarification.",
        doneWhen:
          "This week every review comment arrives through the agreed format, and no task is reopened by a side message.",
        addresses: ["issues:2", "opportunities:2"],
        suggestedOwner: "Priya Dawson",
        whyThisOwner:
          "Priya Dawson can make the rule practical enough to use this week instead of turning it into process theatre.",
        signals: {
          profiles: [
            "The team can follow simple structure when it is explicit.",
          ],
          fundamentals: [
            "Commitment improves when the handoff format is not negotiable.",
          ],
          pulse: [
            "Fragmented feedback is one of the loudest recurring complaints.",
          ],
        },
      },
      {
        id: "step-2",
        title: "List the active work that is already in the red zone",
        whyItMatters:
          "The team needs one honest view of overload before it can reduce it.",
        doneWhen:
          "The team has one list of overloaded work, each item with an owner and the date it started slipping.",
        addresses: ["issues:3"],
        suggestedOwner: "Serena Holt",
        whyThisOwner:
          "Serena Holt is best positioned to collect the practical signal quickly and turn it into a visible decision.",
        signals: {
          profiles: [
            "A small expert team becomes brittle when overload stays implicit.",
          ],
          fundamentals: [
            "Results improve when risk is surfaced before the miss.",
          ],
          pulse: [
            "The team is already signaling strain and late surprises.",
          ],
        },
      },
    ],
    this_week: [
      {
        id: "step-3",
        title: "Publish the decision matrix for what escalates and what does not",
        whyItMatters:
          "This is the fastest way to relieve the approval bottleneck without pretending the team can decentralize everything overnight.",
        doneWhen:
          "The matrix is shared with the whole team, and the next three escalations are checked against it.",
        addresses: ["issues:1"],
        suggestedOwner: "Adrian Cole",
        whyThisOwner:
          "Adrian Cole can define the new boundaries without creating confusion about the final escalation line.",
        signals: {
          profiles: [
            "The team has enough judgment to carry more local decisions.",
          ],
          fundamentals: [
            "Conflict and accountability both improve when boundaries are explicit.",
          ],
          pulse: [
            "People keep waiting on decisions that should be routinized.",
          ],
        },
      },
      {
        id: "step-4",
        title: "Name pilot owners for three recurring operating lanes",
        whyItMatters:
          "A small pilot is the lowest-risk way to prove the team can carry more middle ownership.",
        doneWhen:
          "Each of the three lanes has one named owner, and decisions in those lanes no longer wait for the lead.",
        addresses: ["issues:1", "opportunities:1", "strengths:2"],
        suggestedOwner: "Serena Holt",
        whyThisOwner:
          "Serena Holt can help choose lanes that are real enough to matter but contained enough to learn from quickly.",
        signals: {
          profiles: [
            "The team already has plausible candidates for local ownership.",
          ],
          fundamentals: [
            "Trust is strong enough to support a pilot without heavy resistance.",
          ],
          pulse: [
            "The team wants structure that changes the day-to-day experience, not just language about change.",
          ],
        },
      },
    ],
    this_month: [
      {
        id: "step-5",
        title: "Run one weekly status ritual across active work",
        whyItMatters:
          "A visible weekly rhythm is the simplest way to raise transparency and catch blocked work earlier.",
        doneWhen:
          "Four weeks in a row, every active item has a status, an owner and a next step at the weekly check.",
        addresses: ["opportunities:2", "issues:3"],
        suggestedOwner: "Priya Dawson",
        whyThisOwner:
          "Priya Dawson can keep the ritual short, legible, and close to decisions instead of status theatre.",
        signals: {
          profiles: [
            "Small teams benefit quickly from one visible operating rhythm.",
          ],
          fundamentals: [
            "Commitment and results both improve when owners and blockers stay visible.",
          ],
          pulse: [
            "The team keeps asking for clearer status and fewer late surprises.",
          ],
        },
      },
      {
        id: "step-6",
        title: "Run a focused round of role and growth conversations",
        whyItMatters:
          "This will reduce uncertainty about ownership and help the team see how its potential is meant to be used.",
        doneWhen:
          "Every team member has had one conversation and has a short written note on their role and next growth step.",
        addresses: ["issues:4"],
        suggestedOwner: "Adrian Cole",
        whyThisOwner:
          "Adrian Cole can connect daily role clarity and longer-term growth without splitting them into separate tracks.",
        signals: {
          profiles: [
            "The team has more usable range than the current role clarity suggests.",
          ],
          fundamentals: [
            "Accountability improves when people know what is fully theirs.",
          ],
          pulse: [
            "People are asking for clearer expectations and more visible growth paths.",
          ],
        },
      },
    ],
    this_quarter: [
      {
        id: "step-7",
        title: "Build a lightweight middle layer for recurring coordination and quality calls",
        whyItMatters:
          "Without this, the team will stay trapped between high standards and low scalability.",
        doneWhen:
          "Two people besides the lead and co-lead make recurring coordination and quality calls without escalating them.",
        addresses: ["opportunities:1", "issues:1"],
        suggestedOwner: "Adrian Cole",
        whyThisOwner:
          "Adrian Cole is the natural sponsor for turning pilot ownership into a durable operating layer.",
        signals: {
          profiles: [
            "The team has enough depth to support a real middle layer.",
          ],
          fundamentals: [
            "The lowest operating categories need structural help, not just encouragement.",
          ],
          pulse: [
            "Most recurring pain points point back to missing coordination ownership.",
          ],
        },
      },
      {
        id: "step-8",
        title: "Add capacity control and early-risk escalation to the operating rhythm",
        whyItMatters:
          "Even better roles and cleaner feedback will not hold if overload continues to surface after the team has already absorbed the cost.",
        doneWhen:
          "Overload is raised before work is committed: the weekly check shows capacity, and risks reach the co-lead before a deadline slips.",
        addresses: ["issues:3"],
        suggestedOwner: "Serena Holt",
        whyThisOwner:
          "Serena Holt can connect practical pacing, risk visibility, and cross-team follow-through into one usable loop.",
        signals: {
          profiles: [
            "The team needs more explicit operating guardrails around pace.",
          ],
          fundamentals: [
            "Results improve when risk is raised before the miss, not after it.",
          ],
          pulse: [
            "Stress and late escalation keep showing up together in the source signals.",
          ],
        },
      },
    ],
  },
  sync: {
    providers: ["asana", "notion", "clickup", "linear"],
    lastSync: { provider: "asana", at: "6 Apr 2026, 14:20" },
  },
};

export const sixPillarsCoopProfilePreview = {
  sections: [
    {
      id: "overall-assessment",
      title: "Overall assessment",
      preview:
        "A thoughtful, serious, and promising team, but not yet a fully balanced business unit.",
      paragraphs: [
        "Demo Team is a compact, high-capability core team for a messaging product inside a very large company. The team’s shape is strongest in Reasoning, with Drive and Connection both present, and a lighter but still useful amount of Capacity. In simple terms, this is a team that can think clearly, make decisions, and build sensible product steps. It is well suited to solving hard product problems, setting criteria, and moving work forward without too much noise. That is a strong base for both goals: raising AI-assisted coding to 70% and growing the product to strong monthly revenue growth.",
        "The team is, however, small and functionally concentrated. Reasoning-heavy teams often produce good answers, but they can become too internal, too cautious, or too debate-driven if the operating model is not clear. This matters because messaging products need speed, customer empathy, fast experimentation, and reliable execution at scale. Adrian Cole, Priya Dawson, and Julian Park give the team strong structure and decision quality. Serena Holt and Felix Mercer add user sensitivity and collaboration, which protects the team from becoming too dry or purely technical.",
        "Overall, this is a thoughtful, serious, and promising team, but not yet a fully balanced business unit. It can achieve the goals if you lead it with clear decision rules, tighter commercial focus, and stronger delivery rhythm. Without that, the team may build well, but monetise too slowly.",
      ],
    },
    {
      id: "team-potential",
      title: "Team Potential",
      preview:
        "Strong Reasoning plus enough Drive and Connection to turn thinking into product movement.",
      paragraphs: [
        "The team’s biggest advantage is the combination of strong Reasoning with enough Drive and Connection to turn thinking into product movement. Adrian Cole, Julian Park, and Priya Dawson can help the group define criteria, reduce ambiguity, and keep decisions grounded. This is excellent for a messaging product where quality, trust, reliability, and clear trade-offs matter. The team is likely to perform well in roadmap shaping, system thinking, product definition, and technical judgement.",
        "A second strong point is the presence of Connection-first people such as Serena Holt and Felix Mercer. They can improve the product’s feel, help the team notice user friction earlier, and reduce internal misunderstanding. In a messaging product, this is not a soft extra; it is central. Messaging succeeds when it feels natural, safe, and fast. Serena Holt can help connect design choices to user comfort and trust. Felix Mercer can act as a bridge between product intent and user-facing execution.",
        "To use the team’s strengths well, you should split work clearly: Adrian Cole and Priya Dawson on direction, criteria, and priorities; Julian Park on robust architecture and technical soundness; Serena Holt on user experience and product feel; Felix Mercer on front-end quality, adoption patterns, and workflow polish. If managed well, this team can create a product that is both sensible and usable, which is the right base for revenue growth and AI adoption at the same time.",
      ],
    },
    {
      id: "risk-areas",
      title: "Risk Areas",
      preview:
        "The main risk is overconcentration in Reasoning and underweight commercial and operational coverage.",
      paragraphs: [
        "The main risk is overconcentration in Reasoning and underweight commercial and operational coverage. This team can define what makes sense, but it may spend too long refining answers before testing market response. For a strong monthly revenue growth goal, that is dangerous. Messaging growth often depends on speed, pricing experiments, packaging, partner alignment, growth loops, and very clear ownership of commercial outcomes. Those capabilities are not strongly visible in the current line-up.",
        "There is also a structural gap in Capacity at the top level. Capacity is present, but nobody leads with it. That means the team may under-manage workload, delivery pace, support burden, budget discipline, and sustainable operating rhythm. In a high-pressure environment, this can turn into hidden overload. AI adoption goals can make this worse: if the team is pushed to reach 70% AI-assisted coding without practical rollout, tool support, and training time, the result may be frustration rather than productivity.",
        "Another risk is that the team is too small for the scale of the target. Five people inside a large company can influence a lot, but only if they are deeply focused and supported by adjacent functions. If Demo Team is expected to drive both major revenue growth and engineering behaviour change, then the current setup may be too narrow. You should especially watch for blurred ownership between Adrian Cole and Priya Dawson, design or front-end insight being heard too late, and Julian Park becoming a bottleneck for key technical decisions.",
      ],
    },
    {
      id: "stress-behaviors",
      title: "Stress Behaviors",
      preview:
        "Under stress, the team gets more analytical, more decisive, and less emotionally available.",
      paragraphs: [
        "Under stress, this team is likely to become more analytical, more decisive in a narrow sense, and less emotionally available. Group conversations may shift into “What is correct?” rather than “What is needed now?” That can be useful in technical incidents, but less useful in product uncertainty, conflict, or commercial pressure. When goals become unclear, a Reasoning-heavy team often seeks more criteria, more proof, and more structured argument. This can slow action at exactly the wrong moment.",
        "At the same time, the Drive present in Adrian Cole and Priya Dawson means stress may also produce firmer direction and shorter conversations. That helps in crisis, but it can make Serena Holt and Felix Mercer less vocal if the room becomes too sharp or too solution-driven too quickly. The risk is not open conflict; it is quieter disengagement, missed user signals, and decisions that look rational but are not fully tuned to actual behaviour.",
        "Because Connection is present but not dominant across the whole group, the team may not naturally process tension in real time. Instead, frustration may appear as over-explaining, retreat into specialist work, or reduced collaboration. Julian Park may become more fixed on technical correctness. Adrian Cole may become more directive. Priya Dawson may push toward closure. The team lead should therefore separate “result mode” from “process mode” very clearly, especially in difficult weeks, so pressure does not damage trust or learning.",
      ],
    },
    {
      id: "recommendations",
      title: "Recommendations",
      preview:
        "Create a simple operating system for speed, clarity, and balance.",
      groups: [
        {
          title: "Communication and Management",
          paragraphs: [
            "Your main management task is to create a simple operating system for speed, clarity, and balance. Start each important discussion by stating whether the team is in result mode or process mode. With Adrian Cole, Priya Dawson, and Julian Park, this will reduce circular analysis and help decisions land faster. It will also help Serena Holt and Felix Mercer understand when they are being invited to explore and when they are being asked to commit.",
            "Use role clarity aggressively. Adrian Cole should own technical and team direction. Priya Dawson should own product priorities, business hypotheses, and success criteria. Julian Park should own back-end quality, system risk, and technical feasibility boundaries. Serena Holt should own user experience coherence and design quality. Felix Mercer should own front-end execution quality and interface consistency. If responsibilities overlap, name the overlap explicitly rather than hoping people sort it out themselves.",
            "Tie all work to the two quantitative goals. Keep reviews short and metric-based. If a project does not clearly support stronger monthly revenue growth or the 70% AI-assisted coding target, question whether it deserves time. The team does not need more meetings; it needs cleaner decisions and better sequencing.",
          ],
        },
        {
          title: "Complementary Hires",
          paragraphs: [
            "To balance the team, the first hire should add strong top-level Capacity or strong commercial execution. The best option is a product operations, growth, or business-focused operator with strong Capacity and decent Drive or Reasoning. This person would bring delivery rhythm, experiment throughput, dependency management, and a stronger link to commercial outcomes.",
            "A second strong option is a commercially sharper growth product manager or monetisation lead. Priya Dawson is likely capable on the product side, but the team currently lacks someone whose natural instinct is pricing, packaging, conversion, and market learning. For a messaging product with aggressive revenue goals, that capability matters.",
            "If headcount allows, a third valuable addition would be another engineer with stronger Drive or Capacity than the current technical group. This would help Adrian Cole and Julian Park turn good technical standards into broader team execution rather than carrying too much of that burden themselves.",
          ],
        },
        {
          title: "Support Systems",
          paragraphs: [
            "This team will benefit from support systems that reduce overload and make execution more practical. First, create lightweight templates for decision-making: problem, data, criteria, chosen option, test plan, owner, and review date. This will help Adrian Cole, Priya Dawson, and Julian Park move quickly without over-explaining.",
            "Second, build a safe AI adoption path rather than a hard target alone. For example: which workflows should use AI first, which quality controls are mandatory, what good prompts and review patterns look like, and where humans must stay more involved. Felix Mercer and Julian Park should help codify these patterns, while Adrian Cole ensures they become operating practice rather than optional tips.",
            "Third, protect team energy with visible workload tracking, sprint capacity limits, and recovery rules. Serena Holt and Felix Mercer are likely to feel overload early in a more emotional or behavioural sense; Julian Park may feel it in a quieter and more technical form. A system that makes strain visible will help the whole team stay productive.",
          ],
        },
      ],
    },
    {
      id: "operational-guidance",
      title: "Operational Guidance",
      preview:
        "Some people are safe to leave alone for weeks, others need structure and earlier check-ins.",
      groups: [
        {
          title: "Needs Structure Feedback",
          lists: [{ items: ["Serena Holt", "Felix Mercer"] }],
        },
        {
          title: "Safe to Leave Alone for Weeks",
          lists: [{ items: ["Adrian Cole", "Julian Park", "Priya Dawson"] }],
        },
        {
          title: "Trusted with Autonomy",
          lists: [{ items: ["Adrian Cole", "Julian Park", "Priya Dawson"] }],
        },
        {
          title: "Underused or at Burnout Risk",
          lists: [{ items: ["Serena Holt", "Julian Park", "Felix Mercer"] }],
        },
        {
          title: "Watch Closely",
          lists: [{ items: ["Julian Park", "Serena Holt", "Felix Mercer"] }],
        },
      ],
    },
    {
      id: "care-in-tough-times",
      title: "Care in Tough Times",
      preview:
        "Serena Holt, Felix Mercer, and Julian Park need attention under strain, but for different reasons.",
      paragraphs: [
        "In tough periods, pay closest attention to Serena Holt, Felix Mercer, and Julian Park, but for different reasons. Serena Holt and Felix Mercer are more likely to feel tension in the team climate, unclear expectations, or decisions made too quickly without enough dialogue. They may not always resist directly. Instead, they may go quiet, become less decisive, or spend energy adapting around the team rather than shaping it. As team lead, Adrian Cole should check in with them privately, ask what is becoming harder, and give them clear, bounded ownership so they do not feel pushed aside.",
        "Julian Park needs care for another reason: he is likely to keep functioning well while strain is rising. Reasoning-first, technically responsible people often carry hidden cognitive load. If the team is pursuing AI tooling changes, revenue-critical reliability, and core messaging scale at the same time, Julian Park could become the silent load-bearing point. Watch for over-responsibility, narrowing patience, or reluctance to delegate.",
        "Priya Dawson and Adrian Cole are more likely to stay outwardly stable under pressure, but they can become too forceful or too solution-focused. They do not need protection from stress as much as protection from becoming over-directive. In hard times, your best move is regular one-to-ones, clear priorities, explicit trade-offs, and fewer simultaneous bets. People in this team need clarity more than motivation speeches.",
      ],
    },
    {
      id: "promotion-candidates",
      title: "Promotion Candidates",
      preview:
        "Priya Dawson, Julian Park, and possibly Serena Holt are the clearest promotion cases.",
      paragraphs: [
        "The strongest promotion candidates are Priya Dawson, Julian Park, and possibly Serena Holt, depending on business need. Priya Dawson looks the clearest candidate for broader product leadership because her profile combines Drive and Reasoning. That suggests she can set direction, make decisions, and hold a line under ambiguity. If she has already shown good judgement on priorities and stakeholder handling, she could grow into a larger product group role. The basis for promotion would be her ability to connect product choices to revenue outcomes, not just roadmap quality.",
        "Julian Park is a strong candidate for senior or staff-level technical responsibility if the organisation values technical rigour, system quality, and dependable architecture. His profile suggests strength in criteria, structure, and practical implementation. He should be promoted on the basis of technical leverage: reducing system risk, enabling scale, and helping the team adopt AI in safe, high-value engineering workflows.",
        "Serena Holt may be a good candidate for broader design ownership if the product needs stronger user-centred leadership. Her Connection-Capacity pattern can be very valuable in messaging, where trust, comfort, and interaction quality drive retention and monetisation. The basis for her growth would be not only design craft but also measurable product impact through user understanding.",
        "Adrian Cole may already be at the right level as team lead, but if he is to grow further, it should be through stronger cross-functional business leadership, not only technical authority.",
      ],
    },
    {
      id: "hiring-needs",
      title: "Hiring Needs",
      preview:
        "The team most needs people who strengthen revenue execution, practical delivery rhythm, and market learning.",
      paragraphs: [
        "The team most needs people who strengthen revenue execution, practical delivery rhythm, and market learning. A growth or monetisation product lead would help Priya Dawson and Adrian Cole connect product work to the strong monthly revenue growth target more directly. This person should be comfortable with pricing tests, conversion funnels, usage segmentation, and business cases, not just feature design.",
        "A product operations or programme delivery profile would also add real value. Because the team has no top-level Capacity-heavy person, practical coordination may be weaker than it looks. This hire would help with release rhythm, experiment throughput, dependency management, stakeholder alignment, and team capacity planning. That would support both revenue growth and the 70% AI coding target by making execution more repeatable.",
        "On the engineering side, consider hiring an execution-strong full-stack or platform engineer who is comfortable turning AI-assisted development into team standards. This person should help Adrian Cole and Julian Park spread good practice rather than centralise it. If the messaging product has major growth ambitions, one more engineer may simply be necessary.",
        "If restructuring is possible, create a tighter product pod around Priya Dawson, Serena Holt, Felix Mercer, and one more growth-minded engineer, while Adrian Cole and Julian Park hold the platform, architecture, and AI-enablement layer more clearly.",
      ],
    },
    {
      id: "avoid-hiring",
      title: "Avoid Hiring",
      preview:
        "Avoid more people who add abstract analysis without improving action.",
      paragraphs: [
        "Avoid hiring more people who mainly add abstract analysis without improving action. Another pure Reasoning-first, debate-heavy profile may sound attractive in a technical organisation, but this team already has strong analytical coverage through Adrian Cole, Julian Park, and Priya Dawson. If you add another person who loves criteria but does not naturally drive shipping, customer learning, or commercial experiments, you risk slowing the team further.",
        "Also avoid hiring highly dominant Drive-first people who need to lead everything. The team already has enough directional force in Adrian Cole and Priya Dawson. Another hard-driving personality could create ownership clashes, shorten discussion too much, and reduce the voice of Serena Holt and Felix Mercer. That would hurt design quality and product sensitivity.",
        "Be careful with hires who are very emotionally expressive but weak in structure or delivery. The team would benefit from more customer and market feel, but not at the cost of execution discipline. Messaging products need empathy and judgement together.",
        "In practice, the least useful hire for Demo Team would be someone who is clever, opinionated, and strategic, but weak in operational follow-through. You do not need more smartness in the abstract. You need people who add balance: revenue instinct, rhythm, and delivery capacity.",
      ],
    },
    {
      id: "team-building",
      title: "Team Building",
      preview:
        "The best team-building here is task-based, low-drama, and connected to real work.",
      paragraphs: [
        "The best team-building for Demo Team is task-based, low-drama, and connected to real work. This team is unlikely to benefit much from highly emotional or overly playful formats with no business link. A better option is structured workshops where people solve product problems together in a balanced way. For example, run short sessions where Priya Dawson frames a business challenge, Serena Holt brings user friction, Felix Mercer brings front-end reality, Julian Park brings technical constraints, and Adrian Cole helps define decision criteria. That builds trust through contribution rather than performance.",
        "Use formats that separate result mode and process mode. Start with ten minutes of context and user reality, then move into criteria and decisions, then close with ownership and next steps. This suits both the Reasoning-heavy and Connection-present parts of the team. It allows Serena Holt and Felix Mercer to contribute early, while still giving Adrian Cole, Priya Dawson, and Julian Park the structure they prefer.",
        "Good team-building themes would be: mapping the ideal messaging user journey, reviewing failed experiments without blame, or co-designing the AI-assisted development workflow. Avoid forced vulnerability exercises. Instead, build safety through better collaboration habits, visible appreciation of different strengths, and small wins. For this team, shared clarity is more bonding than social theatre.",
      ],
    },
    {
      id: "quick-wins",
      title: "Quick Wins",
      preview:
        "The fastest gains come from clearer meetings, clearer bets, and clearer AI standards.",
      lists: [
        {
          style: "actionable",
          items: [
            "Ask Adrian Cole to introduce a simple rule for every meeting: say whether it is result mode or process mode at the start.",
            "Have Priya Dawson define three revenue-critical bets for the product and link each one to a metric, owner, and review date.",
            "Ask Julian Park and Adrian Cole to publish an AI coding playbook with approved use cases, review standards, and examples.",
            "Give Serena Holt explicit ownership of two or three key user experience problems tied to retention or conversion.",
            "Ask Felix Mercer to measure where AI helps front-end work most and where it creates rework, then feed that into team standards.",
            "Create a one-page decision template so the team spends less time repeating analysis.",
            "Run one monthly product review focused only on monetisation and adoption signals.",
            "Create a visible owner map for priorities, design decisions, technical standards, and release readiness.",
          ],
        },
      ],
    },
    {
      id: "long-term-plan",
      title: "Long-Term Plan",
      preview:
        "Build the team into a more balanced product-and-revenue pod over the next 6 to 12 months.",
      paragraphs: [
        "Over the next 6 to 12 months, build Demo Team into a more balanced product-and-revenue pod. First, stabilise the operating model: clear owners, cleaner decision rules, visible metrics, and a shared AI-assisted engineering standard. Adrian Cole should lead the execution system, while Priya Dawson leads the business and product learning loop. Julian Park should codify scalable technical patterns. Serena Holt and Felix Mercer should be pulled earlier into product shaping, not only later into design and interface delivery.",
        "Second, reorganise work around a small number of monetisation and adoption streams. For example: messaging retention, paid business use cases, and AI-enabled development efficiency. Each stream should have one accountable owner, clear measures, and a fast experiment cycle. The team must not chase too many ideas at once.",
        "Third, add one or two complementary hires: a growth or monetisation lead, and ideally an operations or execution-focused role. This would rebalance the team away from pure analysis toward practical throughput and market impact.",
        "Finally, make learning visible. Review monthly what increased revenue, what improved AI-assisted coding quality, what reduced delivery friction, and where the team’s shape is still creating blind spots. If you do this well, Demo Team can become a very strong specialist team: technically sound, user-aware, commercially sharper, and much more scalable than it is today.",
      ],
    },
    {
      id: "swot-summary",
      title: "SWOT Summary",
      preview:
        "The team has strong analytical depth, but it needs more operational and commercial force.",
      groups: [
        {
          title: "Opportunities",
          lists: [
            {
              items: [
                "Use the team’s strong Reasoning to create a high-quality AI-assisted development standard and become a model team inside the wider company.",
                "Turn Serena Holt’s and Felix Mercer’s user sensitivity into stronger retention, adoption, and messaging experience quality.",
                "Support Priya Dawson with growth or monetisation capability to convert solid product thinking into stronger revenue outcomes.",
                "Use the product’s position inside a very large company to access partnership channels, distribution, or internal credibility that smaller competitors do not have.",
                "Build a reputation as the team that combines product quality with disciplined AI-supported execution.",
              ],
            },
          ],
        },
        {
          title: "Strengths",
          lists: [
            {
              items: [
                "Strong Reasoning coverage through Adrian Cole, Julian Park, and Priya Dawson supports clear thinking, criteria, and sound decision-making.",
                "Enough Drive in Adrian Cole and Priya Dawson gives the team direction and helps decisions move forward.",
                "Serena Holt and Felix Mercer add Connection, which improves user sensitivity, collaboration, and product feel.",
                "The team is small, focused, and likely able to align quickly once priorities are clear.",
                "The product context — messaging plus AI adoption — suits the team’s natural strengths in systems, product shaping, and workflow design.",
              ],
            },
          ],
        },
        {
          title: "Threats",
          lists: [
            {
              items: [
                "The team may build thoughtful solutions that are too slow to capture revenue opportunities.",
                "Julian Park or Adrian Cole could become bottlenecks if too many technical and strategic decisions centralise around them.",
                "Pressure to reach 70% AI-assisted coding may create compliance theatre if rollout is not supported with practical tools and guardrails.",
                "Because the team sits inside a large company, external dependencies and stakeholder load could easily overwhelm a five-person core team.",
                "If Serena Holt and Felix Mercer are underused in shaping product decisions, the product may become technically strong but emotionally weaker for users.",
              ],
            },
          ],
        },
        {
          title: "Weaknesses",
          lists: [
            {
              items: [
                "Too much concentration in analytical strength can lead to overthinking, slower experimentation, and internally focused debates.",
                "No strong top-level Capacity profile means workload, delivery rhythm, budget realism, and sustainable pace may be under-managed.",
                "Commercial and monetisation capability is not strong enough for a strong monthly revenue growth target without added support.",
                "The team is small relative to the scale of both targets, which increases fragility.",
                "Role boundaries between product, design, and technical direction may not yet be clear enough to support fast, autonomous execution.",
              ],
            },
          ],
        },
      ],
    },
  ] as PreviewSection[],
} as const;

export const sixPillarsFundamentalsPreview = {
  strongestArea: {
    category: "Commitment",
    average: 4.24,
    interpretation:
      "Commitment is a real strength. The team appears clear on what matters, can decide without perfect information, and then stays the course. This supports speed, reduces rework, and helps everyone focus. The main improvement area is not commitment itself, but making commitments more visible and easier to track, so accountability becomes stronger too.",
    statements: [
      {
        text: "The team is clear about its direction and priorities.",
        meta: "4.5",
      },
      {
        text: "The team sticks to decisions.",
        meta: "4.5",
      },
      {
        text: "The team is decisive, even when perfect information is not available.",
        meta: "4.25",
      },
    ],
    recommendations: {
      quickWins: [
        "Introduce a shared ‘decision & actions’ note after each meeting: decision, owner, due date, success measure.",
        "At the end of meetings, ask each person: ‘What are you committing to before our next check-in?’",
        "Use a clear ‘definition of done’ for key deliverables so commitment is concrete, not just verbal.",
        "When there is disagreement, ask for a quick ‘commitment statement’: ‘I disagreed because…, and I commit by doing…’",
      ],
      longTerm: [
        "Build a consistent meeting rhythm: weekly tactical (30–60 mins), monthly strategic (2–3 hours), quarterly planning (half day).",
        "Use the Coop profile test results to agree working agreements (e.g., how you prefer decisions to be made, how much detail you need, how you want to be challenged).",
        "Create a small set of annual and quarterly priorities, and connect sprint goals to them so commitment stays aligned at every level.",
        "Run a quarterly ‘commitment retro’: review where the team changed direction, why, and what signals you missed earlier.",
      ],
    },
  },
  weakestArea: {
    category: "Accountability",
    average: 3.63,
    interpretation:
      "Accountability is the lowest category. This does not look like a ‘bad’ team; it looks like a team that is respectful and supportive, but sometimes too careful. The result can be slower correction of issues, unclear standards, and a reliance on self-management instead of helpful peer challenge. Over time, this can weaken results and increase frustration.",
    statements: [
      {
        text: "Team members are quick to confront peers about problems in their respective areas of responsibility.",
        meta: "3.25",
      },
      {
        text: "The team ensures that members feel pressure from their peers and the expectation to perform.",
        meta: "3.25",
      },
      {
        text: "Team members question one another about their current approaches and methods.",
        meta: "3.5",
      },
    ],
    recommendations: {
      quickWins: [
        "Agree what ‘good’ looks like for core work (quality bar, response times, review expectations) and write it down in one page.",
        "Start a ‘lightning round’ in weekly meetings: each person shares top focus + one risk, others ask one direct question.",
        "Adopt a simple peer challenge rule: it is acceptable to ask ‘What’s your plan?’ and ‘What do you need from us?’ without it being seen as criticism.",
        "Use a ‘no surprises’ expectation: if a deadline or quality risk appears, flag it early (even if you do not have the full answer yet).",
        "Pair up ‘accountability buddies’ for two weeks at a time to check progress and remove blockers (10 minutes, twice a week).",
      ],
      longTerm: [
        "Run the Team Effectiveness Exercise quarterly: each person receives one strength and one improvement point from peers, focused on impact on the team.",
        "Build a culture of regular feedback: short, kind, specific, and close to the moment (not saved for performance reviews).",
        "Shift from individual ownership to shared ownership on key outcomes (e.g., product quality, delivery reliability) so peer pressure becomes natural.",
        "Train the team in a shared feedback model (e.g., SBI: Situation–Behaviour–Impact) and practise with real examples.",
        "Introduce team-based rewards/recognition for hitting shared goals (this increases healthy peer expectation).",
      ],
    },
  },
  completion: {
    percent: 80,
    answered: 4,
    available: 5,
  },
  /** Answers across the cycles the page shows, as on the product's stage. */
  shownAnswers: {
    completed: 26,
    total: 30,
  },
  schedule: {
    previousCycle: "2026-02-02",
    nextCycle: "2026-04-06",
    rhythm: "Every two months",
  },
  categories: [
    { category: "Trust", average: 4.13 },
    { category: "Conflict", average: 4.13 },
    { category: "Commitment", average: 4.24 },
    { category: "Accountability", average: 3.63 },
    { category: "Results", average: 4.03 },
  ] as FundamentalsCategoryScore[],
  sections: [
    {
      id: "strongest-area",
      title: "The Strongest Areas",
      preview:
        "Commitment is the current anchor: the team knows what matters and sticks to decisions.",
      paragraphs: [
        "Commitment is a real strength. The team appears clear on what matters, can decide without perfect information, and then stays the course. This supports speed, reduces rework, and helps everyone focus.",
      ],
      lists: [
        {
          label: "Top Statements",
          items: [
            {
              text: "The team is clear about its direction and priorities.",
              meta: "4.5",
            },
            {
              text: "The team sticks to decisions.",
              meta: "4.5",
            },
            {
              text: "The team is decisive, even when perfect information is not available.",
              meta: "4.25",
            },
          ],
        },
        {
          label: "Quick Wins",
          items: [
            "Introduce a shared decision and actions note after each meeting.",
            "Use a clear definition of done for key deliverables.",
            "End meetings by asking each person what they are committing to before the next check-in.",
          ],
        },
      ],
    },
    {
      id: "weakest-area",
      title: "The Weakest Areas",
      preview:
        "Accountability is the softest category: peer challenge is present, but not yet consistent.",
      paragraphs: [
        "Accountability is the lowest category. The team follows through and gives feedback, but it is less consistent in quickly confronting problems in a peer's area and questioning each other's methods.",
      ],
      lists: [
        {
          label: "Bottom Statements",
          items: [
            {
              text: "Team members are quick to confront peers about problems in their respective areas of responsibility.",
              meta: "3.25",
            },
            {
              text: "The team ensures that members feel pressure from their peers and the expectation to perform.",
              meta: "3.25",
            },
            {
              text: "Team members question one another about their current approaches and methods.",
              meta: "3.5",
            },
          ],
        },
        {
          label: "Quick Wins",
          items: [
            "Use the 24-hour rule for missed deadlines and quality risks.",
            "Introduce peer questions as normal: What's your plan? What's the risk? What do you need?",
            "Review progress against goals in every weekly meeting.",
          ],
        },
      ],
    },
    {
      id: "key-differences",
      title: "Areas of Key Differences",
      preview:
        "The team does not experience standards and healthy dissent in the same way.",
      groups: [
        {
          title: "Observed Statements",
          lists: [
            {
              items: [
                {
                  text: "26. All members of this team are held to the same high standards.",
                  meta: "1 · 1 · 4 · 4 · 4 · 4 · 2 · 2",
                },
                {
                  text: "15. Team members willingly make sacrifices in their areas for the good of the team.",
                  meta: "1 · 4 · 4 · 4 · 4 · 5 · 5 · 5",
                },
                {
                  text: "23. Team members communicate unpopular opinions to the group.",
                  meta: "1 · 1 · 2 · 2 · 3 · 3 · 3 · 5",
                },
                {
                  text: "1. Team members admit their mistakes.",
                  meta: "1 · 2 · 2 · 3 · 3 · 3 · 4 · 5",
                },
                {
                  text: "21. The team ensures that members feel pressure from their peers and the expectation to perform.",
                  meta: "1 · 2 · 2 · 3 · 4 · 4 · 4 · 4",
                },
              ],
            },
          ],
        },
        {
          title: "Implications",
          paragraphs: [
            "These differences suggest the team does not experience the culture in the same way. Some people feel standards are clear and applied equally, while others do not. Some people find it easy to share unpopular views; others may hold back. This can create quiet frustration: a few people may carry the ‘speaking up’ and ‘high standards’ load, while others assume things are fine. If not addressed, this often leads to misunderstandings, uneven workload, and slower decisions.",
          ],
        },
        {
          title: "Recommendations",
          lists: [
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Hold a 45-minute session to agree ‘team standards’: what quality, pace, and behaviours you expect from everyone (then publish it).",
                "In meetings, use a ‘round robin’ for key decisions so each person must share a view (including doubts).",
                "Introduce a ‘red flag’ phrase anyone can use (e.g., ‘I have a concern’) which pauses the meeting and invites discussion.",
                "Do a short retro after tough meetings: ‘What was hard to say? What did we avoid?’",
                "Make sacrifices visible: when someone shifts priorities for the team, name it and explain the benefit.",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Use the Coop profile test to explore why people experience standards and conflict differently (e.g., direct vs careful communicators, risk tolerance, need for certainty).",
                "Build psychological safety around mistakes: leaders and senior members model ‘Here’s what I got wrong’ regularly.",
                "Create a fair peer review approach (e.g., code/design/product reviews) with shared criteria so ‘high standards’ becomes consistent and not personal.",
                "Run a quarterly anonymous pulse on the key difference items (26, 23, 1, 21) to track whether gaps are closing.",
                "Coach the team on ‘healthy dissent’: how to disagree strongly and still stay respectful and aligned afterwards.",
              ],
            },
          ],
        },
      ],
    },
    {
      id: "recommendations-by-category",
      title: "Recommendations by Category",
      preview:
        "Trust, conflict, commitment, accountability, and results all have specific operating moves now.",
      groups: [
        {
          title: "Trust",
          badge: "High",
          tone: "positive",
          lists: [
            {
              label: "Meaning",
              items: [
                "The team generally feels safe and open with each other, especially around asking for help, sharing input, and being genuine. One weaker spot is admitting mistakes, which is often the real test of vulnerability when stakes are high.",
              ],
            },
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Add a weekly ‘mistake and learning’ moment (one small thing, no blame).",
                "Agree the rule: mistakes are discussed as ‘what happened’ and ‘what we will change’, not ‘who failed’.",
                "Reduce gossip by bringing issues to the room: ‘If it affects the team, we discuss it together.’",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Run Coop profile sessions to build empathy around working styles and stress reactions.",
                "Do the Personal Histories Exercise (or a light version) once per quarter to deepen connection.",
                "Consider a simple 360-style peer feedback process focused on development only (not pay), once per year.",
              ],
            },
          ],
        },
        {
          title: "Conflict",
          badge: "High",
          tone: "positive",
          lists: [
            {
              label: "Meaning",
              items: [
                "The team can debate and surface issues, which supports better decisions. The main risk is uneven participation: a few people may be comfortable challenging, while others stay quieter, especially with unpopular opinions.",
              ],
            },
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Create conflict norms and keep them visible in meetings.",
                "Use ‘steel-manning’: before disagreeing, summarise the other person’s view fairly.",
                "Ask ‘What would we do if we were wrong?’ to invite challenge without blame.",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Train the team in facilitation skills so conflict does not depend on one person.",
                "Build a habit of decision reviews: what we decided, what we learned, what we will adjust.",
                "Use Coop profile insights to tailor how you debate (some need space; some prefer live discussion).",
              ],
            },
          ],
        },
        {
          title: "Commitment",
          badge: "High",
          tone: "positive",
          lists: [
            {
              label: "Meaning",
              items: [
                "Clarity and buy-in are strong, and the team supports decisions even after disagreement. This is a competitive advantage. The growth opportunity is to keep commitment linked to clear actions and measures, so it feeds accountability and delivery reliability.",
              ],
            },
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Finish meetings with: decisions, owners, deadlines, and what ‘done’ means.",
                "Use a shared action tracker that is reviewed weekly (lightweight, not bureaucratic).",
                "Repeat priorities at the start of the week to reduce shifting focus.",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Adopt a thematic goal for each quarter (one most important priority).",
                "Build a ‘cascading communication’ habit so wider teams receive one clear message.",
                "Run quarterly planning sessions that connect strategy to delivery and capacity.",
              ],
            },
          ],
        },
        {
          title: "Accountability",
          badge: "Medium",
          tone: "warning",
          lists: [
            {
              label: "Meaning",
              items: [
                "Follow-through is good, but peer-to-peer challenge is not yet consistent. The team may hesitate to question methods or confront issues quickly, which can create delays and uneven standards. Improving this should reduce stress and protect results.",
              ],
            },
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Use the 24-hour rule for missed deadlines and quality risks.",
                "Introduce peer questions as normal: ‘What’s your plan?’ ‘What’s the risk?’ ‘What do you need?’",
                "Review progress against goals in every weekly meeting (short and consistent).",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Run the Team Effectiveness Exercise quarterly to normalise peer feedback.",
                "Publish clear team standards (delivery, quality, behaviours) and revisit them monthly.",
                "Link recognition to ‘team accountability moments’ (when someone challenged early and helped the team win).",
              ],
            },
          ],
        },
        {
          title: "Results",
          badge: "High",
          tone: "positive",
          lists: [
            {
              label: "Meaning",
              items: [
                "The team cares about collective outcomes and is willing to make trade-offs for success. This is a strong sign of maturity. The biggest risk is that without stronger accountability, results could slip due to small issues not being addressed early.",
              ],
            },
            {
              label: "Quick Wins",
              style: "actionable",
              items: [
                "Create a visible scoreboard of the team’s 3–5 most important results.",
                "Do a monthly results review: what moved, what did not, and why.",
                "Celebrate behaviours that protect results (raising risks early, helping another function).",
              ],
            },
            {
              label: "Long Term",
              style: "actionable",
              items: [
                "Ensure goals are stable and not shifting without a clear reason and communication plan.",
                "Consider team-based rewards/recognition to reinforce collective success.",
                "Use quarterly retrospectives to connect behaviours (trust/conflict/accountability) to actual performance outcomes.",
              ],
            },
          ],
        },
      ],
    },
    {
      id: "statement-averages",
      title: "Statements Averages",
      preview:
        "The spread confirms what the team feels: a strong commitment core with softer accountability.",
      lists: [
        {
          label: "Lowest Averages",
          items: [
            {
              text: "Team members are quick to confront peers about problems in their respective areas of responsibility.",
              meta: "3.25",
            },
            {
              text: "The team ensures that members feel pressure from their peers and the expectation to perform.",
              meta: "3.25",
            },
            {
              text: "Team members question one another about their current approaches and methods.",
              meta: "3.5",
            },
          ],
        },
        {
          label: "Highest Averages",
          items: [
            {
              text: "Team members offer unprovoked, constructive feedback to one another.",
              meta: "5.0",
            },
            {
              text: "All members of this team are held to the same high standards.",
              meta: "4.75",
            },
            {
              text: "Team members value collective success more than individual achievement.",
              meta: "4.75",
            },
          ],
        },
      ],
    },
  ] as PreviewSection[],
} as const;

// The stage figures (average, extremes, completion) are computed from these cycles in
// lib/six-pillars-stage.ts; the latest cycle is the one the Overview date follows.
export const sixPillarsPulsePreview = {
  cycles: [
    { startsOn: "2026-02-13", ok: 2, notOk: 2, expected: 4, isOpen: false },
    { startsOn: "2026-02-20", ok: 3, notOk: 1, expected: 4, isOpen: false },
    { startsOn: "2026-02-27", ok: 2, notOk: 1, expected: 4, isOpen: false },
    { startsOn: "2026-03-06", ok: 4, notOk: 0, expected: 4, isOpen: false },
    { startsOn: "2026-03-13", ok: 3, notOk: 1, expected: 4, isOpen: false },
    { startsOn: "2026-03-20", ok: 2, notOk: 2, expected: 4, isOpen: false },
    { startsOn: "2026-03-27", ok: 3, notOk: 1, expected: 4, isOpen: false },
    { startsOn: "2026-04-03", ok: 3, notOk: 1, expected: 4, isOpen: false },
  ] as PulseStageCycle[],
  schedule: {
    nextPulse: "Apr 10, 9:00 AM",
    rhythm: "Weekly",
  },
  feedbackCounts: {
    ok: 6,
    bad: 4,
  },
  feedback: {
    ok: [
      {
        id: "#1",
        when: "13.03.2026",
        text: "Sprint priorities stayed stable and reviews came back quickly.",
      },
      {
        id: "#2",
        when: "13.03.2026",
        text: "Infra work landed quietly without blocking delivery.",
      },
      {
        id: "#3",
        when: "20.03.2026",
        text: "Design handoff was cleaner than usual.",
      },
      {
        id: "#4",
        when: "27.03.2026",
        text: "Planning was lighter and the batch of work felt realistic.",
      },
      {
        id: "#5",
        when: "27.03.2026",
        text: "The week had fewer interrupts than usual.",
      },
      {
        id: "#6",
        when: "03.04.2026",
        text: "Runbooks made the production change easy to support.",
      },
    ],
    bad: [
      {
        id: "#1",
        when: "13.03.2026",
        text: "Frontend copy changed late, which forced a rushed UI pass.",
      },
      {
        id: "#2",
        when: "20.03.2026",
        text: "Ownership between product fixes and platform work felt blurry.",
      },
      {
        id: "#3",
        when: "27.03.2026",
        text: "A deployment dependency surfaced later than it should have.",
      },
      {
        id: "#4",
        when: "03.04.2026",
        text: "Feedback moved after code was already in review.",
      },
    ],
  } as Record<"ok" | "bad", PulseFeedbackItem[]>,
} as const;

export const sixPillarsCandidatesPreview = {
  sections: [
    {
      id: "summary",
      title: "Summary",
      preview:
        "A believable fit if the role stays concrete and ownership is made explicit.",
      paragraphs: [
        "James Bremer looks like a believable fit for Demo Team if the role stays concrete and ownership is made explicit. The profile pattern suggests someone who can contribute well in an engineering-heavy environment with clear expectations and a visible review cadence.",
      ],
    },
    {
      id: "current-team-vs-candidate",
      title: "Current Team vs Candidate",
      preview:
        "The team needs steadier backend ownership more than another floating helper.",
      lists: [
        {
          label: "Team Pattern",
          items: [
            "Engineering demo team already has credible range across delivery, coordination, and specialist work. What it still needs most is steadier backend ownership.",
          ],
        },
        {
          label: "Candidate Pattern",
          items: [
            "James Bremer adds depth without feeling like a duplicate seat. The profile reads like someone who can absorb context, contribute with discipline, and strengthen the team once they understand how decisions really move.",
          ],
        },
      ],
    },
    {
      id: "will-this-work-out",
      title: "Will This Work Out",
      preview:
        "Likely yes, provided the first 30 days are structured around a real ownership lane.",
      lists: [
        {
          label: "Short Answer",
          items: [
            "Likely yes, provided the first 30 days are structured around a real ownership lane instead of vague support.",
          ],
        },
        {
          label: "Watch for",
          items: [
            "Unclear role boundaries that force the candidate to guess where ownership starts and ends.",
            "Feedback that stays too high-level instead of using examples from live work, review notes, or operating habits.",
            "Too many parallel workstreams before the candidate understands how the team actually coordinates.",
          ],
        },
      ],
    },
    {
      id: "role-and-profile-fit",
      title: "Role and Profile Fit",
      preview:
        "Strongest in work that rewards dependable follow-through, signal gathering, and thoughtful trade-offs.",
      paragraphs: [
        "James Bremer appears strongest in work that rewards dependable follow-through, signal gathering, and thoughtful trade-offs. They are less likely to thrive in a role where success depends on constantly reverse-engineering shifting expectations.",
      ],
    },
    {
      id: "team-impact",
      title: "Team Impact",
      preview:
        "This hire should reduce over-reliance on the lead for day-to-day clarity.",
      lists: [
        {
          label: "Positive",
          items: [
            "Should strengthen backend ownership and reduce over-reliance on the lead for day-to-day clarity.",
            "Adds another profile-complete voice to planning, handoffs, and practical team decisions.",
          ],
        },
        {
          label: "Risk",
          items: [
            "If the team mistakes seniority for instant context, the hire may look slower than they really are during the first few weeks.",
          ],
        },
      ],
    },
    {
      id: "onboarding-plan",
      title: "Onboarding Plan",
      preview:
        "Week one should make role boundaries explicit before the first ownership lane begins.",
      lists: [
        {
          label: "Week 1",
          items: [
            "Make role boundaries explicit and introduce the handful of recurring rituals that actually matter.",
            "Shadow one live workflow from intake to review so the candidate can see how this team makes decisions in practice.",
          ],
        },
        {
          label: "Days 30-60",
          items: [
            "Give the candidate one clear ownership lane with visible trade-offs and named stakeholders.",
            "Review not just output, but how they navigate ambiguity, escalation, and team rhythm.",
          ],
        },
      ],
    },
    {
      id: "hire-recommendations",
      title: "Hire Recommendations",
      preview:
        "Proceed when the role really needs another durable owner, not another floating helper.",
      lists: [
        {
          label: "Proceed if",
          items: [
            "The role truly needs another durable owner rather than another floating helper.",
            "The lead is willing to give crisp context, not just broad expectations.",
          ],
        },
        {
          label: "Hold if",
          items: [
            "The team still cannot explain where this person would own versus assist.",
            "There is no clean onboarding path for how the candidate should learn the operating system of the team.",
          ],
        },
      ],
    },
    {
      id: "bottom-line",
      title: "Bottom Line",
      preview:
        "A credible hire when the role is framed around clear ownership and practical collaboration.",
      paragraphs: [
        "James Bremer is a credible hire for this demo team. The fit is strongest when the role is framed around clear ownership, steady feedback, and practical collaboration rather than abstract help where needed.",
      ],
    },
  ] as PreviewSection[],
} as const;

export const sixPillarsRandomCoffeePreview = {
  current: {
    id: "current-backend",
    weekStart: "2026-05-18",
    format: "With a team member",
    partnerName: "Julian Park",
    partnerRole: "Engineer",
    partnerEmail: "julian.park@example.com",
    teamName: "Demo Team",
    profileLabel: "Engineer - R / Ca / C / D",
    reason:
      "Adrian Cole and Julian Park have a useful chance to compare where technical decisions are speeding the team up and where product context needs to be made clearer.",
    conversationGuide:
      "Start with one recent decision, keep it practical, and let the chat stay easy.",
    workTopics: [
      "Where technical context is helping decisions move faster.",
      "How to spot handoffs that might turn into rework.",
      "Small engineering habits that made the week calmer.",
    ],
    lifestyleTopics: [
      "A film, show, book, or podcast that stayed with you recently.",
      "A weekend routine or habit that helps you reset.",
      "A place, meal, song, or small discovery you would recommend.",
    ],
  },
  history: [
    {
      id: "history-frontend",
      weekStart: "2026-05-11",
      format: "With a team member",
      partnerName: "Felix Mercer",
      partnerRole: "Front-End Developer",
      partnerEmail: "felix.mercer@example.com",
      teamName: "Demo Team",
      profileLabel: "Poet - C / R / D / Ca",
      reason:
        "Adrian Cole and Felix Mercer can swap notes on shifting interface expectations and where a cleaner review loop helps the whole team.",
      conversationGuide:
        "Pick one UI moment from last week and treat it as a shared work story, not a critique.",
      workTopics: [
        "How to catch moving interface requirements earlier.",
        "What makes review useful instead of merely fast.",
        "Where frontend and leadership can align expectations sooner.",
      ],
      lifestyleTopics: [
        "A small way to decompress after a dense week.",
        "A recent recommendation for music, food, or a walk.",
        "Something ordinary that unexpectedly improved the week.",
      ],
    },
    {
      id: "history-product",
      weekStart: "2026-05-04",
      format: "With a team member",
      partnerName: "Priya Dawson",
      partnerRole: "Product Manager",
      partnerEmail: "priya.dawson@example.com",
      teamName: "Demo Team",
      profileLabel: "Enforcer - D / R / Ca / C",
      reason:
        "Adrian Cole and Priya Dawson should have an easy conversation about product priority and delivery rhythm so context gets stuck between roles less often.",
      conversationGuide:
        "Start with one moment where a product choice changed engineering work, then leave room for normal human context.",
      workTopics: [
        "Which product signals engineers need earlier.",
        "Where the team benefits from a clearer decision owner.",
        "How to make planning lighter without losing useful context.",
      ],
      lifestyleTopics: [
        "A good coffee, lunch, or walking spot near work.",
        "What helped you reset after a tricky meeting.",
        "A film, game, book, or trip you would happily talk about.",
      ],
    },
    {
      id: "history-design",
      weekStart: "2026-04-27",
      format: "With a team member",
      partnerName: "Serena Holt",
      partnerRole: "Designer",
      partnerEmail: "serena.holt@example.com",
      teamName: "Demo Team",
      profileLabel: "Empath - C / Ca / D / R",
      reason:
        "Adrian Cole and Serena Holt have useful stories to trade about design handoffs and where a clearer review path makes delivery feel calmer.",
      conversationGuide:
        "Talk through one recent handoff concretely, then make space for lighter topics too.",
      workTopics: [
        "Design decisions worth naming before implementation starts.",
        "Where a review path removes stress from a handoff.",
        "How to make interface dependencies more visible to the team.",
      ],
      lifestyleTopics: [
        "A hobby or ritual that helps clear your head.",
        "A recent recommendation from a friend or colleague.",
        "A place you would go back to on a free day.",
      ],
    },
  ],
  // Julian's "30 seconds" deck as the product writes it for his team lead
  // (cooperly_app lang/en/coop_profile_cards.php, Engineer, `lead` perspective).
  currentDeck: {
    glyph: "▣",
    headline: "Ask if the yes is a real yes.",
    reason: "May go along outwardly while still disagreeing.",
    functionLines: {
      R: "Structure first",
      Ca: "Build and fix together",
      C: "Calm tone, no sarcasm",
      D: "Low need to lead",
    },
    do: [
      {
        title: "Lay out the issue, facts and next steps",
        detail: "Skip the long build-up before the point.",
      },
      {
        title: "Frame disagreement as a logic check",
        detail: "A clean argument earns respect, even in a dispute.",
      },
      {
        title: "Under stress, shrink it to the next step",
        detail: "Ask what you know, what you don’t, what’s next.",
      },
    ],
    dont: [
      {
        title: "Use sarcasm or digs in front of others",
        detail: "Give corrections in private, calmly.",
      },
      {
        title: "Say “you must” or “because I said so”",
        detail: "It can cause a shutdown or quiet detachment.",
      },
      {
        title: "Interrupt a thought before it is finished",
        detail: "Let the thought land, then reply.",
      },
    ],
  },
} as const satisfies {
  current: RandomCoffeePreviewInvite;
  history: readonly RandomCoffeePreviewInvite[];
  currentDeck: RandomCoffeePreviewDeck;
};

export function getSixPillarsPreviewPerson(name: string) {
  return sixPillarsPreviewPeople.find((person) => person.name === name);
}

export function getPreviewEvidenceCount(signals: PreviewSignalGroups) {
  return (
    signals.profiles.length +
    signals.fundamentals.length +
    signals.pulse.length
  );
}
